import { RequestContext, GetDetailPageInput, QueryParameters } from 'DVWebNodeUtils'
import { DEFAULT_PAGE_SIZE, getCollectionCurrentOffset } from '../components/collection/pagination'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { int, Omit, stringifyOnClient } from '../utils/utils'
import { EPISODE_START_INDEX } from '../components/episodeList/episodeList'
import { SECOND_EXPANDER_THRESHOLD } from '../reducers/collections'

interface IGetDetailPageInput {
    context: RequestContext,
    titleID: string,
    sections: WpsRuntime.atv.wps.SectionTypeList,
    isCrow?: boolean
    widgets?: WpsRuntime.atv.wps.Widgets,
    widgetsConfig?: WpsRuntime.atv.wps.WidgetsConfig,
    amabotContext?: WpsRuntime.atv.wps.AmabotContext,
}

export type GetAtfInput = Pick<GetDetailPageInput, 'context' | 'titleID' | 'isCrow' | 'isElcano'>
export type GetBtfInput = Pick<GetDetailPageInput, 'context' | 'titleID' | 'isElcano'>
export type GetDetailPageInputWithoutContext = Omit<GetDetailPageInput, 'context'>
export type GetAtfInputWithoutContext = {
    titleID: string,
    atfWidgets?: WpsRuntime.atv.wps.AtfWidgetsList,
    isElcano: boolean
}

// TODO(AVWebDetailPage-3428): Merge getAtfInputWithoutContext and getAtfInput
export const getAtfInputWithoutContext = ({ titleID, atfWidgets, isElcano }: GetAtfInputWithoutContext): GetDetailPageInputWithoutContext => {

    const input = {
        titleID,
        sections: ['Atf'],
        isElcano
    } as GetDetailPageInputWithoutContext

    if (atfWidgets) {
        input.widgets = stringifyOnClient({ atf: atfWidgets }) as WpsRuntime.atv.wps.Widgets
    }

    return input
}

export const getAtfInput = (args: GetAtfInput): GetDetailPageInput => {
    const input: IGetDetailPageInput = { ...args, sections: ['Atf'] }
    let extraWidgets: WpsRuntime.atv.wps.AtfWidgetType[] = []

    return addWidgetsToAtfInput(input, extraWidgets)
}

export const getEpisodeListSize = (parameters: QueryParameters): number | undefined => {
    const episodeListSize = parameters && parameters.episodeListSize && parameters.episodeListSize[0]
    return episodeListSize ? int(episodeListSize) : undefined
}

export const getBtfInput = (args: GetBtfInput, useEpisodeListSorting = false): GetDetailPageInput => {
    const startIndex =
      useEpisodeListSorting? EPISODE_START_INDEX : getCollectionCurrentOffset(args.context.queryParameters, 'episodes')
    const pageSize =
      useEpisodeListSorting? (getEpisodeListSize(args.context.queryParameters) || SECOND_EXPANDER_THRESHOLD) : DEFAULT_PAGE_SIZE

    return getBtfEpisodesInput(args, getDefaultBtfWidgets(), startIndex, pageSize)
}

export const getBtfEpisodesInput = (args: GetBtfInput, widgetList: string[], startIndex: number, pageSize: number): GetDetailPageInput => {

    return {
        ...args,
        sections: ['Btf'],
        widgets: stringifyOnClient({ 'btf': widgetList }) as WpsRuntime.atv.wps.Widgets,
        widgetsConfig: stringifyOnClient({
            episodes: {
                startIndex,
                pageSize
            },
            bonus: {
                startIndex: getCollectionCurrentOffset(args.context.queryParameters, 'bonus'),
                pageSize: DEFAULT_PAGE_SIZE
            }
        }) as WpsRuntime.atv.wps.WidgetsConfig
    }
}

// Additional is a temporary widget which helps with the migration of Gurupa widgets to 3TA.
// Its widgets belong to BTF and that's why it requests the BTF section of WPS
export const getAdditionalInput = (args: GetBtfInput, useFaq: boolean, useContactUs: boolean): GetDetailPageInput => {
    let input: IGetDetailPageInput = { ...args, sections: ['Btf'] }
    let extraWidgets: WpsRuntime.atv.wps.BtfWidgetType[] = []

    if (useFaq) {
        extraWidgets.push('Faq')
    }

    if (useContactUs) {
        extraWidgets.push('ContactUs')
    }

    return addWidgetsToAdditionalInput(input, extraWidgets)
}

const addWidgetsToAtfInput = (input: IGetDetailPageInput, extraWidgets: WpsRuntime.atv.wps.AtfWidgetType[]) => {
    if (extraWidgets.length == 0) {
        return input
    }

    const atfWidgets = input.widgets && input.widgets.atf ? input.widgets.atf : getDefaultAtfWidgets()
    atfWidgets.push(...extraWidgets)

    return {
        ...input,
        widgets: stringifyOnClient({ 'atf': atfWidgets }) as WpsRuntime.atv.wps.Widgets,
    }
}

const addWidgetsToAdditionalInput = (input: IGetDetailPageInput, extraWidgets: WpsRuntime.atv.wps.BtfWidgetType[]) => {
    const btfWidgets = input.widgets && input.widgets.btf ? input.widgets.btf : getDefaultAdditionalWidgets()
    btfWidgets.push(...extraWidgets)

    return {
        ...input,
        widgets: stringifyOnClient({ 'btf': btfWidgets }) as WpsRuntime.atv.wps.Widgets,
    }
}

// We use functions here instead of constants in order to ensure the immutability of the arrays
export const getDefaultAtfWidgets = () => ['Header', 'BuyBox', 'Watchlist', 'Restrictions', 'Notification', 'Trailer', 'SeasonSelector', 'Self', 'Banner', 'BottomBar', 'Creative', 'BundleCarousel']
export const getDefaultBtfWidgets = () => ['Episodes', 'Bonus', 'Schedule', 'Highlights', 'Restrictions']
export const getDefaultAdditionalWidgets = () => ['ProductDetails', 'CastAndCrew', 'ScoutBanner', 'Restrictions', 'OtherFormats', 'Carousels']
